import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { quoteService, getErrorMessage } from '@/services';
import type { QuoteFilters, QuoteCreateRequest } from '@/types';

// Wave F1 item 4a: how long to keep polling for synchronously-priced quotes
// before treating an empty result as terminal (never hang the compare screen
// forever). Pricing normally completes inside the submit POST itself, so this
// window only matters for the QUOTES_DISPATCH_ASYNC=True case.
const QUOTES_POLL_INTERVAL_MS = 1500;
const QUOTES_POLL_TIMEOUT_MS = 6000;

export function useQuotes(page: number = 1, filters?: QuoteFilters) {
  return useQuery({
    queryKey: ['quotes', page, filters],
    queryFn: () => quoteService.getQuotes(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Item C7: the Insurance Type / Insurance Company options actually present on
 * the CALLER's own quote requests (`/quotes/requests/filter-options/`,
 * `QuoteRequestViewSet.filter_options`) -- used by MyQuotesPage instead of
 * the platform-wide `useInsuranceTypesLookup` lookup, for the same
 * cross-tenant-leak reason `useCustomerPolicyFilterOptions` (item C5) exists.
 * Every viewer of MyQuotesPage is a customer (it is the customer-portal-only
 * page), so this is unconditional here -- there is no other-role branch to
 * preserve the way PoliciesListPage has one.
 */
export function useQuoteFilterOptions() {
  return useQuery({
    queryKey: ['quotes', 'filter-options'],
    queryFn: () => quoteService.getFilterOptions(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useQuote(id: string) {
  return useQuery({
    queryKey: ['quote', id],
    queryFn: () => quoteService.getQuote(id),
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QuoteCreateRequest) => quoteService.createQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuoteCreateRequest> }) =>
      quoteService.updateQuote(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', variables.id] });
      toast.success('Quote updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quoteService.deleteQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Quote deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Replacement for the broken useQuoteStream socket.io hook (Wave F1 item 4a):
 * polls the persisted Quote rows for a QuoteRequest via the existing
 * broker-scoped REST endpoint instead of a WebSocket stream that can't work
 * without an ASGI/Channels worker in this prod. Since pricing runs
 * SYNCHRONOUSLY during the submit POST, the first fetch usually already has
 * the final result; polling only covers the (currently unused)
 * QUOTES_DISPATCH_ASYNC=True path, and stops after QUOTES_POLL_TIMEOUT_MS so
 * a request with zero eligible/adapter-backed insurers resolves to a clean
 * "no quotes available" state instead of spinning forever.
 */
export function useQuotesForRequest(requestId: string | null) {
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    startedAtRef.current = Date.now();
  }, [requestId]);

  const query = useQuery({
    queryKey: ['insurer-quotes', requestId],
    queryFn: () => quoteService.getInsurerQuotesForRequest(requestId as string),
    enabled: !!requestId,
    refetchInterval: (q) => {
      const data = q.state.data ?? [];
      if (data.length > 0) return false;
      if (Date.now() - startedAtRef.current > QUOTES_POLL_TIMEOUT_MS) return false;
      return QUOTES_POLL_INTERVAL_MS;
    },
  });

  const quotes = query.data ?? [];
  const timedOut = Date.now() - startedAtRef.current > QUOTES_POLL_TIMEOUT_MS;
  // Complete once we have at least one quote, or once we've given the
  // synchronous path a fair chance and it came back empty (no eligible
  // insurer / no adapter for this line) — never left "fetching" forever.
  const isComplete = quotes.length > 0 || (timedOut && !query.isFetching);

  return {
    quotes,
    isLoading: query.isLoading,
    isComplete,
    refetch: query.refetch,
  };
}

export function useSubmitQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quoteService.submitQuote(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quote', id] });
      toast.success('Quote submitted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
